import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";
import { useDocumentSeo } from "../lib/seo";

export default function AdminLogin() {
    useDocumentSeo({ title: "Admin Login — TT Admins", noindex: true });
    const { user, loading, login, startGoogleSignIn } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const navigate = useNavigate();

    if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="animate-spin" /></div>;
    if (user) return <Navigate to="/admin" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await login(email, password);
            navigate("/admin");
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center dawn-wash p-6" data-testid="admin-login-page">
            <form onSubmit={onSubmit} className="faith-card p-8 w-full max-w-md">
                <div className="w-14 h-14 rounded-2xl bg-[var(--brand-navy)] text-white flex items-center justify-center mb-6 mx-auto">
                    <Lock />
                </div>
                <h1 className="font-serif text-3xl text-center text-[var(--brand-navy)] mb-2">Admin Portal</h1>
                <p className="text-sm text-center text-slate-500 mb-8">Sign in to manage your site.</p>
                {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4" data-testid="login-error">{err}</div>}
                <label className="block mb-4">
                    <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Email</span>
                    <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--brand-gold)]" data-testid="login-email" />
                </label>
                <label className="block mb-6">
                    <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Password</span>
                    <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--brand-gold)]" data-testid="login-password" />
                </label>
                <button type="submit" disabled={busy} className="btn-gold w-full disabled:opacity-50" data-testid="login-submit-btn">
                    {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in...</> : "Sign In"}
                </button>

                <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                    <div className="relative flex justify-center"><span className="bg-white px-3 text-xs uppercase tracking-wider text-slate-400 font-semibold">Or</span></div>
                </div>

                <button
                    type="button"
                    onClick={() => startGoogleSignIn("/admin")}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors"
                    data-testid="admin-google-signin-btn"
                >
                    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    </svg>
                    Sign in with Google
                </button>
                <p className="text-xs text-center text-slate-400 mt-3">
                    Google sign-in only grants admin access if you use the authorized admin email.
                </p>
            </form>
        </div>
    );
}
